import {
  Button,
  FunnelStepHeader,
  FunnelWithHelper,
  QuoteDerogationLockNotice,
  SectionedFormBuilder,
  areFieldsDirty,
  buildAllFieldsFromSections,
  buildFormValuesFromFields,
  buildPayloadFromFields,
  getResolver,
  useStepperContext,
} from '@folio/components';
import { useTranslation } from 'react-i18next';
import { FormBuilderFooter } from '@folio/components/forms/form-builder/FormBuilderFooter';
import { useCurrentFirmType } from '@folio/utils/useCurrentFirmType';
import { usePolicyOnDashboard } from '@folio/utils/usePolicyOnDashboard';
import { shouldDisableQuoteFields } from '@folio/utils/quoteDerogationLock';
import { useQuery } from '@tanstack/react-query';
import {
  clientWithFork,
  customerControllerGetCustomerQueryOptions,
  getNewBranchIdAfterFork,
  keyValueFieldsToObject,
  policyControllerGetPolicyQueryOptions,
  usePolicyControllerEditPolicy,
  viewSpecificControllerListPolicyAssetsQueryOptions,
} from 'korint-sdk';
import type { FormSections, FormSectionsWithShortcuts } from 'korint-sdk/react';
import type { AfterSubmitFn } from '../buildFieldCleanup';
import { useResolvedEntitySections } from '../shared-fields/use-entity-fields';
import { useForm, useFormState, type UseFormReturn } from 'react-hook-form';
import { useEffect, useRef } from 'react';
import { useParams } from 'react-router-dom';
import { Stack } from '@folio/components/ui/flex';

type ApproveAllCheckboxesButtonProps = {
  fieldNames: string[];
  form: UseFormReturn<Record<string, unknown>>;
  hidden?: boolean;
};

function ApproveAllCheckboxesButton({ fieldNames, form, hidden }: ApproveAllCheckboxesButtonProps) {
  const { t } = useTranslation();
  if (!fieldNames.length || hidden) {
    return null;
  }

  const handleApproveAll = () => {
    for (const name of fieldNames) {
      form.setValue(name, true, { shouldDirty: true });
    }
  };

  return (
    <Button variant="outline" onClick={handleApproveAll}>
      {t('policyFunnel.approveAll')}
    </Button>
  );
}

type PolicyFunnelModuleProps = {
  /**
   * The sections to display in the form.
   * It's a list of sections, each section contains a list of fields and a title.
   */
  sections: FormSectionsWithShortcuts;

  /**
   * The step path in the funnel (e.g. 'policy'). Used to compute section title
   * translation keys: `${productId}.policyFunnel.${path}.sections.${firstField}.title`.
   */
  path?: string;

  /**
   * Whether to show the previous button.
   * Defaults to true.
   */
  showPreviousButton?: boolean;

  /**
   * The mode of the form.
   * Defaults to 'stepper'.
   */
  mode?: 'standalone' | 'stepper';

  /**
   * Optional callback invoked after `updatePolicy` and before `stepper.next()`. Mirrors the
   * `afterSubmit` prop of `CustomerFunnelModule` for symmetry. Typically used to perform
   * conditional cleanups on the policy custom fields.
   */
  afterSubmit?: AfterSubmitFn;
  helper?: React.ReactNode;
};

export function PolicyFunnelModule({
  sections: rawSections,
  path,
  mode = 'stepper',
  showPreviousButton = true,
  afterSubmit,
  helper,
}: PolicyFunnelModuleProps) {
  const { t } = useTranslation();
  const { policyId, branchId, customerId } = useParams();
  if (!policyId) {
    throw new Error('No policy id found in the url');
  }

  const { mutateAsync: updatePolicy } = usePolicyControllerEditPolicy({
    client: { client: clientWithFork },
  });

  const {
    data: policy,
    isLoading: isPolicyLoading,
    refetch,
  } = useQuery({
    ...policyControllerGetPolicyQueryOptions(policyId, { branchId }),
    select: data => ({
      ...data,
      customFields: keyValueFieldsToObject(data.customFields ?? []),
    }),
  });

  const { data: customer, isLoading: isCustomerLoading } = useQuery({
    ...customerControllerGetCustomerQueryOptions(customerId!, { branchId }),
    enabled: Boolean(customerId),
    select: data => ({
      ...data,
      customFields: keyValueFieldsToObject(data.customFields ?? []),
    }),
  });

  const { data: firstAsset } = useQuery({
    ...viewSpecificControllerListPolicyAssetsQueryOptions(policyId, { branchId }),
    enabled: Boolean(policyId),
    select: data => {
      const asset = data.data?.[0];
      if (!asset) return undefined;
      return { ...asset, customFields: keyValueFieldsToObject(asset.customFields ?? []) };
    },
  });

  const productId = policy?.productId;
  const { sections: resolvedSections, isLoading: isProductConfigLoading } =
    useResolvedEntitySections({ entityType: 'policy', productId, sections: rawSections });

  const sections = resolvedSections.map(section => {
    const firstFieldName = section.fields?.[0]?.name;
    const autoTitle =
      productId && path && firstFieldName
        ? t(`${productId}.policyFunnel.${path}.sections.${firstFieldName}.title`, {
            defaultValue: '',
          })
        : '';
    const autoDescription =
      productId && path && firstFieldName
        ? t(`${productId}.policyFunnel.${path}.sections.${firstFieldName}.description`, {
            defaultValue: '',
          })
        : '';
    return {
      ...section,
      title: autoTitle || section.title || undefined,
      description: autoDescription || section.description || undefined,
    };
  });

  const allFields = buildAllFieldsFromSections(sections);

  const form = useForm({
    mode: 'onChange',
    resolver: getResolver(allFields),
    values: buildFormValuesFromFields(allFields, policy),
    // Same as CustomerFunnelModule: the `policy` refetch and the product-config query both land after mount
    // and change `values`, resetting form and wiping fields just typed.
    resetOptions: { keepDirtyValues: true },
    context: {
      customer,
      policy,
    },
  });

  const sectionsWithApproveAll: FormSections = sections.map(section => {
    const checkboxFieldNames =
      section.fields
        ?.filter(field => field.type === 'checkbox' && !field.hidden)
        .map(field => field.name) ?? [];

    if (!section.enableApproveAllButton || checkboxFieldNames.length === 0) {
      return section;
    }

    const approveAllButton = (
      <ApproveAllCheckboxesButton
        key={`${section.title ?? 'section'}-approve`}
        fieldNames={checkboxFieldNames}
        form={form}
      />
    );

    if (!section.submitButton) {
      return {
        ...section,
        submitButton: approveAllButton,
      };
    }

    return {
      ...section,
      submitButton: (
        <>
          {section.submitButton}
          <span className="ml-2 inline-flex">{approveAllButton}</span>
        </>
      ),
    };
  });

  const { workingBranch } = usePolicyOnDashboard();
  const { firmType } = useCurrentFirmType();
  const disableQuoteFields = shouldDisableQuoteFields(workingBranch, firmType);

  const stepper = useStepperContext();

  const stepperRef = useRef(stepper);
  stepperRef.current = stepper;
  const { isDirty } = useFormState({ control: form.control });
  // biome-ignore lint/correctness/useExhaustiveDependencies: stepperRef.current avoids stale closure without creating a dep cycle
  useEffect(() => {
    if (isDirty) stepperRef.current?.invalidateFutureSteps?.();
  }, [isDirty]);

  const onSubmit = form.handleSubmit(async data => {
    const { dirtyFields } = form.formState;
    const isDirty = areFieldsDirty(allFields, dirtyFields);

    let mutationBranchId: string | undefined = branchId;

    if (isDirty) {
      const response = await updatePolicy({
        policyId,
        params: { branchId },
        data: buildPayloadFromFields(allFields, data, dirtyFields),
      });

      const newBranchId = getNewBranchIdAfterFork(response);
      if (newBranchId) {
        mutationBranchId = newBranchId;
      }
    }

    refetch();

    if (afterSubmit && customerId) {
      const result = await afterSubmit(data, {
        customerId,
        policyId,
        branchId: mutationBranchId ?? branchId!,
        isDirty,
      });
      mutationBranchId = result?.branchId ?? mutationBranchId;
    }

    if (mode === 'stepper' && stepper) {
      stepper.next(mutationBranchId ? { branchId: mutationBranchId } : undefined);
    }
  });

  return (
    <Stack gap={6}>
      <FunnelStepHeader
        title={t('funnelSteps.policy.title')}
        subtitle={t('funnelSteps.policy.subtitle', '')}
      />
      <FunnelWithHelper helper={helper}>
        {disableQuoteFields && <QuoteDerogationLockNotice />}
        <SectionedFormBuilder
          sections={sectionsWithApproveAll}
          form={form}
          isLoading={isPolicyLoading || isCustomerLoading || isProductConfigLoading}
          externalData={{ policy, customer, asset: firstAsset }}
          disabled={disableQuoteFields}
        />
      </FunnelWithHelper>
      <FormBuilderFooter
        mode={mode}
        onSubmit={onSubmit}
        form={form}
        showPreviousButton={showPreviousButton}
      />
    </Stack>
  );
}
